import { produce } from 'immer'
import { clamp as _clamp, get as _get, pick as _pick, set as _set } from 'lodash'

import { NO_COLOR, getSvgFilterTransformCss } from '@flowx/ui-theme'

import { PropertiesSection, Property, PropertyCollection, Theme } from './'
import { ColorTokens } from './colors'
import { ShadowOption, ShadowTokens } from './shadows'
import { TypographyGroupOption, TypographyOption, TypographyTokens } from './typography'

import { Section, SectionComponent } from '../../constants/sections.constants'
import { Platform, ThemePlatformPathMap } from '../../model/platform.model'
import { SECTIONS_WITHOUT_PLATFORM, ThemePathsMap } from '../../model/sections.model'
import { getShadowStyles } from '../../sections/shadows/shadows.helpers'
import { ColorGroupOption, ColorOption } from '../../types/color.types'

export function getThemePathForPlatform(
  path: Section | SectionComponent | string,
  platform: Platform
): string {
  const pathIsWithoutPlatform = SECTIONS_WITHOUT_PLATFORM.some((section) =>
    path.startsWith(section)
  )
  // not a predefined section path, just a path string
  if (!ThemePathsMap[path]) {
    return `${pathIsWithoutPlatform ? '' : `${ThemePlatformPathMap[platform]}.`}${path}`
  }
  return pathIsWithoutPlatform
    ? ThemePathsMap[path]
    : `${ThemePlatformPathMap[platform]}.${ThemePathsMap[path]}`
}

export function getThemeDataFromPath<T>(theme: Theme, path: string): T | null {
  const themeData: T = _get<Theme, string>(theme, path) // FIXME: see if we need deepClone here
  return themeData || null
}

export function getInheritedPropertyValue(
  theme: Theme,
  platform: Platform,
  property?: string
): Property | null {
  // only concerned with inherited properties
  if (!property?.startsWith('inheritance@')) {
    return null
  }
  const inheritancePath = property.split('inheritance@')[1]
  const inheritedProperty = getThemeDataFromPath<Property>(
    theme,
    `${ThemePlatformPathMap[platform]}.${inheritancePath}`
  )

  if (inheritedProperty?.token) {
    return {
      default: {
        token: inheritedProperty.token,
      },
    }
  }
  if (inheritedProperty?.value) {
    return {
      default: {
        value: inheritedProperty.value,
      },
    }
  }

  // go recursive, if the inherited property is its self inherited from another property
  return (
    getInheritedPropertyValue(
      theme,
      platform,
      inheritedProperty?.default.value || inheritedProperty?.default.token
    ) || inheritedProperty
  )
}

export function getPropertyWithCalculatedValues(
  theme: Theme,
  platform: Platform,
  property?: Property
): Property | null {
  if (property?.default?.token) {
    const inheritedProperty = getInheritedPropertyValue(theme, platform, property.default.token)
    return produce(property, (draft) => {
      // update property, if inherited
      if (inheritedProperty) {
        draft.default.token = inheritedProperty.default.token
      }
      // COLOR OFFSETS - only applies to tokens
      if (draft.default.offset !== undefined) {
        const propSplit = draft.default.token.split('#')
        const newValue = _clamp(parseInt(propSplit[1], 10) + draft.default.offset, 100, 900)
        draft.default.token = `${propSplit[0]}#${newValue}`
        delete draft.default.offset
      }
      // FONT MODIFIERS - only applies to tokens
      if (draft.default.d !== undefined) {
        const propSplit = draft.default.token.split('#')
        draft.default.token = `${propSplit[0]}#${draft.default.d}`
        delete draft.default.d
      }
    })
  }
  if (property?.default?.value) {
    const inheritedProperty = getInheritedPropertyValue(theme, platform, property.default.value)
    if (inheritedProperty) {
      return produce(property, (draft) => {
        draft.default.value = inheritedProperty.default.value
      })
    }
  }
  return property || null
}

export function getPropertyValue(property: Property): string | null | undefined {
  if (!property) {
    return undefined
  }
  if (property.value !== undefined) {
    return property.value
  }
  if (property.token !== undefined) {
    return property.token
  }
  if (property.default?.value !== undefined) {
    return property.default.value
  }
  if (property.default?.token !== undefined) {
    return property.default.token
  }
  return undefined
}

export function getPropertiesSubsetValues(
  properties: Record<string, string>,
  propertiesSubset: readonly string[] | string[]
): Record<string, string> {
  return _pick(properties, propertiesSubset)
}

export function updateThemeAtPath<T>(theme: Theme, path: string, newData: T): Theme {
  return produce(theme, (draft) => {
    _set(draft, path, newData)
  })
}

// merges a section (e.g. icons) into the destination, only overwriting keys that
// already exist in the destination - preserves dest-only keys, adds no source-only keys
export function mergeSectionBetweenPaths<T extends Record<string, unknown>>(
  theme: Theme,
  fromFullPath: string,
  toFullPath: string
): Theme {
  const fromData = getThemeDataFromPath<T>(theme, fromFullPath) ?? ({} as T)
  const toData = getThemeDataFromPath<T>(theme, toFullPath) ?? ({} as T)
  const merged = produce(toData, (draft) => {
    Object.keys(draft).forEach((key) => {
      if (fromData[key] !== undefined) {
        ;(draft as Record<string, unknown>)[key] = fromData[key]
      }
    })
  })
  return updateThemeAtPath(theme, toFullPath, merged)
}

export function updatePropertyWithOverride(
  theme: Theme,
  platform: Platform,
  targetProperty: Property,
  overrideProperty: Property
): Property {
  // overrides will always be applied to 'token' or 'value' props (not 'default', which is readonly, for reset purposes)
  return produce(targetProperty, (property) => {
    // check if override prop is already modified (has value or token)
    if (overrideProperty.token !== undefined) {
      property.token = overrideProperty.token
    } else if (overrideProperty.value !== undefined) {
      property.value = overrideProperty.value
    }
    // if not modified, apply the 'value'/'token' from the 'default' - it's actual value, not the inherited string
    else if (overrideProperty.default.token !== undefined) {
      const updatedProp = getPropertyWithCalculatedValues(theme, platform, overrideProperty)
      property.token = updatedProp.default.token
    } else if (overrideProperty.default.value !== undefined) {
      const updatedProp = getPropertyWithCalculatedValues(theme, platform, overrideProperty)
      property.value = updatedProp.default.value
    }
  })
}

export function updatePropertiesWithValues(
  theme: Theme,
  platform: Platform,
  properties: PropertyCollection,
  newValues: Record<string, string | number>
): PropertyCollection {
  return produce(properties, (draft) => {
    Object.entries(newValues).forEach(([key, value]) => {
      draft[key] = updatePropertyWithValue(theme, platform, draft[key], value)
    })
  })
}

export function updatePropertyWithValue(
  theme: Theme,
  platform: Platform,
  property: Property,
  newValue: string | number
): Property {
  const newProperty = produce(property, (draft) => {
    const propertyWithValues = getPropertyWithCalculatedValues(theme, platform, draft)
    if (
      propertyWithValues?.default.token !== undefined &&
      propertyWithValues.default.token !== newValue
    ) {
      draft.token = newValue?.toString() || null
    } else {
      delete draft.token // if token and default token are the same, delete the token
    }
    if (
      propertyWithValues?.default.value !== undefined &&
      propertyWithValues.default.value !== newValue
    ) {
      draft.value = newValue?.toString() ?? null
    } else {
      delete draft.value // if value and default value are the same, delete the value
    }
  })
  if (newValue === NO_COLOR) {
    return produce(newProperty, (draft) => {
      delete draft.token
      draft.value = NO_COLOR
    })
  }
  return newProperty
}

export function updateThemePropertiesAtPathWithValues(
  theme: Theme,
  platform: Platform,
  path: string,
  newData: Record<string, string | number>
): Theme {
  const propertiesPath = getThemeDataFromPath<PropertiesSection>(theme, path)
  const targetProps = produce(propertiesPath, (draft) => {
    if (draft) {
      Object.entries(newData).forEach(([propName, prop]) => {
        if (draft[propName]) {
          draft[propName] = updatePropertyWithValue(theme, platform, draft[propName], prop)
        }
      })
    }
  })
  return updateThemeAtPath<PropertiesSection>(theme, path, targetProps)
}

export interface ThemeModifierUpdate {
  modifier: string
  properties: Record<string, string | number>
  modifiers?: Array<{ modifier: string; properties: Record<string, string | number> }>
}

export interface ThemePropertiesModifiersIconsUpdate {
  properties?: Record<string, string | number>
  // two levels of modifiers deep, as the theme schema defines them
  modifiers?: ThemeModifierUpdate[]
  icons?: Record<string, string>
}

/**
 * Applies new values to the properties, the (two levels of) modifier properties and the icons of a
 * section, at the section path for `platform`.
 */
export function updateThemePropertiesModifiersIconsAtPath(
  theme: Theme,
  platform: Platform,
  path: string,
  { properties, modifiers, icons }: ThemePropertiesModifiersIconsUpdate
): Theme {
  const sectionPath = getThemePathForPlatform(path, platform)
  const withValues = (
    current: Theme,
    subPath: string,
    values: Record<string, string | number>
  ): Theme =>
    updateThemePropertiesAtPathWithValues(current, platform, `${sectionPath}.${subPath}`, values)

  const modifierUpdates = (modifiers ?? []).flatMap(
    ({ modifier, properties: own, modifiers: nested }) => [
      { subPath: `modifiers.${modifier}.properties`, values: own },
      ...(nested ?? []).map(({ modifier: nestedModifier, properties: nestedProperties }) => ({
        subPath: `modifiers.${modifier}.modifiers.${nestedModifier}.properties`,
        values: nestedProperties,
      })),
    ]
  )
  const updates = [
    ...(properties ? [{ subPath: 'properties', values: properties }] : []),
    ...modifierUpdates,
    ...(icons ? [{ subPath: 'icons', values: icons }] : []),
  ]
  return updates.reduce(
    (current, { subPath, values }) => withValues(current, subPath, values),
    theme
  )
}

export function copySectionToPlatform<T>(
  theme: Theme,
  fromPlatform: Platform,
  toPlatform: Platform,
  sectionPath: Section | SectionComponent | string
): Theme {
  const fromPlatformPath = getThemePathForPlatform(sectionPath, fromPlatform)
  const toPlatformSectionPath = getThemePathForPlatform(sectionPath, toPlatform)
  const fromPlatformData = getThemeDataFromPath<T>(theme, fromPlatformPath)
  return updateThemeAtPath(theme, toPlatformSectionPath, fromPlatformData)
}

export function copyDataPropertiesFromPathToPlatform(
  theme: Theme,
  fromPlatform: Platform,
  toPlatform: Platform,
  path: string,
  propertiesSubset?: readonly string[] | string[]
): Theme {
  const fromPlatformPath = getThemePathForPlatform(path, fromPlatform)
  const fromPlatformData = getThemeDataFromPath<PropertiesSection>(theme, fromPlatformPath)
  const toPlatformPath = getThemePathForPlatform(path, toPlatform)
  const toPlatformData = getThemeDataFromPath<PropertiesSection>(theme, toPlatformPath)
  const copiedData = produce(toPlatformData, (data) => {
    if (data?.properties && fromPlatformData?.properties) {
      Object.entries(data.properties)
        .filter(([propName]) => data.properties[propName] && fromPlatformData.properties[propName])
        .forEach(([propName]) => {
          if (!propertiesSubset || propertiesSubset.includes(propName)) {
            data.properties[propName] = updatePropertyWithOverride(
              theme,
              fromPlatform,
              data.properties[propName],
              fromPlatformData.properties[propName]
            )
          }
        })
    }
  })
  return updateThemeAtPath(theme, toPlatformPath, copiedData)
}

export function copyDataPropertiesBetweenPaths(
  theme: Theme,
  platform: Platform,
  fromSectionPath: string,
  toSectionPath: string,
  propertiesSubset?: readonly string[] | string[]
): Theme {
  const fromFullPath = getThemePathForPlatform(fromSectionPath, platform)
  const toFullPath = getThemePathForPlatform(toSectionPath, platform)
  const fromData = getThemeDataFromPath<PropertiesSection>(theme, fromFullPath)
  const toData = getThemeDataFromPath<PropertiesSection>(theme, toFullPath)
  const copiedData = produce(toData, (data) => {
    if (data?.properties && fromData?.properties) {
      Object.entries(data.properties)
        .filter(([propName]) => data.properties[propName] && fromData.properties[propName])
        .forEach(([propName]) => {
          if (!propertiesSubset || propertiesSubset.includes(propName)) {
            data.properties[propName] = updatePropertyWithOverride(
              theme,
              platform,
              data.properties[propName],
              fromData.properties[propName]
            )
          }
        })
    }
  })
  return updateThemeAtPath(theme, toFullPath, copiedData)
}

export function hasResetForPropertiesAtPath(
  theme: Theme,
  platform: Platform,
  path: string
): boolean {
  const propertiesPath = getThemePathForPlatform(path, platform)
  const properties = getThemeDataFromPath<PropertyCollection>(theme, propertiesPath)
  if (!properties) {
    return false
  }
  return hasResetForProperties(properties)
}

export function hasResetForProperties(props?: Record<string, Property>): boolean {
  return !Object.entries(props ?? {}).every(
    ([, prop]) => prop?.token === undefined && prop?.value === undefined
  )
}

export function resetPropertiesAtPath(theme: Theme, platform: Platform, path: string): Theme {
  const propertiesPath = getThemePathForPlatform(path, platform)
  const properties = getThemeDataFromPath<PropertyCollection>(theme, propertiesPath)
  const resetProperties = produce(properties, (data) => {
    if (data) {
      Object.entries(data).forEach(([, prop]) => {
        delete prop.token
        delete prop.value
      })
    }
  })
  return updateThemeAtPath(theme, propertiesPath, resetProperties)
}

export function resetSpecificPropertiesAtPath(
  theme: Theme,
  platform: Platform,
  path: string,
  propertyNames: readonly string[]
): Theme {
  const propertiesPath = getThemePathForPlatform(path, platform)
  const properties = getThemeDataFromPath<PropertyCollection>(theme, propertiesPath)
  const resetProperties = produce(properties, (data) => {
    if (data) {
      propertyNames.forEach((propName) => {
        const prop = data[propName]
        if (prop) {
          delete prop.token
          delete prop.value
        }
      })
    }
  })
  return updateThemeAtPath(theme, propertiesPath, resetProperties)
}

export function getStyleForProperty(theme: Theme, platform: Platform, property?: Property): string {
  let propertyStyle = ''
  if (!property) {
    return propertyStyle
  }
  if (property.value || property.token) {
    const { value, token, unit } = property
    propertyStyle = `${value ?? token}${unit ?? ''}`
  } else {
    const propertyWithValues = getPropertyWithCalculatedValues(theme, platform, property)
    if (propertyWithValues) {
      const { default: { value, token } = {}, unit } = propertyWithValues
      propertyStyle = (value ?? token) ? `${value ?? token}${unit ?? ''}` : ''
    }
  }
  if (propertyStyle.startsWith('color@')) {
    const colors = getColorHexMap(theme)
    propertyStyle = colors[propertyStyle] ?? ''
  } else if (propertyStyle.startsWith('typography@')) {
    const typography = getTypographyValuesMap(theme, platform)
    propertyStyle = typography[propertyStyle] ?? ''
  } else if (propertyStyle.startsWith('shadow@')) {
    const shadows = getShadowValuesMap(theme, platform)
    propertyStyle = shadows[propertyStyle] ?? ''
  }
  return propertyStyle
}

export function getStyleForProperties(
  theme: Theme,
  platform: Platform,
  properties: Property[]
): Record<string, string> {
  return Object.entries(properties).reduce(
    (acc, [key, property]) => {
      acc[key] = getStyleForProperty(theme, platform, property)
      // TODO: super temporary
      if (key.toLowerCase().includes('icon')) {
        acc[key] = getSvgFilterTransformCss(acc[key])
      }
      return acc
    },
    {} as Record<string, string>
  )
}

export function getShadowValuesMap(theme: Theme, platform: Platform): Record<string, string> {
  const shadowsPath = getThemePathForPlatform('tokens.shadows', platform)
  const shadows = getThemeDataFromPath<ShadowTokens>(theme, shadowsPath)
  if (!shadows) {
    return {}
  }
  const colors = getColorHexMap(theme)
  return Object.entries(shadows).reduce<Record<string, string>>((acc, [key, shadow]) => {
    const hex = colors[shadow.color.token] ?? '#000000'
    acc[`shadow@${key}`] = getShadowStyles(shadow, hex)
    return acc
  }, {})
}

export function getColorHexMap(theme: Theme): Record<string, string> {
  const colors = getThemeDataFromPath<ColorTokens>(theme, 'tokens.colors')
  if (!colors) {
    return {}
  }
  return Object.entries(colors)
    .map(([name, color]) =>
      Object.entries(color['light']).reduce<Record<string, string>>((acc, [shade, hex]) => {
        acc[`color@${name}#${shade}`] = hex
        return acc
      }, {})
    )
    .reduce((acc, curr) => ({ ...acc, ...curr }), {})
}

export function getColorOptions(colors: ColorTokens): ColorOption[] {
  let shades0
  const colorOptions = Object.entries(colors)
    .map(([name, color]) => {
      const parsedColor = Object.entries(color['light']).map(([shade, hex]) => ({
        label: `${color.name} (${shade})`,
        hex,
        value: `color@${name}#${shade}`,
      }))
      // Remove shades(0) from shades array since Shades(0) is transparent and move it one layer up to color categories
      if (name === 'shades') {
        shades0 = parsedColor.shift()
        shades0.label = 'Transparent'
      }
      return parsedColor
    })
    .flat()
  // Add shades(0) as first option and change the label to 'Transparent'
  return [shades0, ...colorOptions]
}

export function getColorOptionGroups(colors: ColorTokens): ColorGroupOption[] {
  if (!colors) {
    return []
  }

  let shades0

  const colorOptions = Object.entries(colors).map(([name, color]) => {
    const parsedColor = Object.entries(color['light']).map(([shade, hex]) => ({
      label: `${color.name} ${shade}`,
      value: {
        hex,
        token: `color@${name}#${shade}`,
      },
    }))
    // Remove shades(0) from shades array since Shades(0) is transparent and move it one layer up to color categories
    if (name === 'shades') {
      shades0 = parsedColor.shift()
      shades0.label = 'Transparent'
    }
    return {
      label: color.name,
      value: {
        hex: color['light']['500'],
      },
      children: parsedColor,
    }
  })
  return [...colorOptions, shades0]
}

export function getTypographyOptions(typography: TypographyTokens): TypographyOption[] {
  return Object.entries(typography)
    .map(([key, type]) =>
      Object.entries(type.weights).map(([weightKey]) => ({
        label: `${type.name} - ${weightKey}`,
        value: `typography@${key}#${weightKey}`,
      }))
    )
    .flat()
}

export function getTypographyOptionGroups(typography: TypographyTokens): TypographyGroupOption[] {
  return Object.entries(typography).map(([key, type]) => {
    const children = Object.entries(type.weights).map(([weightKey]) => ({
      label: `${type.name} - ${weightKey}`,
      value: `typography@${key}#${weightKey}`,
    }))

    return {
      label: type.name,
      children,
    }
  })
}

export function getShadowOptions(shadows: ShadowTokens): ShadowOption[] {
  return Object.entries(shadows).map(([key, shadow]) => ({
    label: `Shadow ${shadow.name}`,
    value: `shadow@${key}`,
  }))
}

export function getTypographyValuesMap(theme: Theme, platform: Platform): Record<string, any> {
  const typographyPath = getThemePathForPlatform('tokens.typography', platform)
  const typography = getThemeDataFromPath<TypographyTokens>(theme, typographyPath)
  if (!typography) {
    return {}
  }
  return Object.entries(typography)
    .map(([name, typographyProp]) =>
      Object.entries(typographyProp.weights).map(([weightName, weightValue]) => ({
        [`typography@${name}#${weightName}`]: {
          fontFamily: typographyProp.styles.fontFamily,
          fontSize: `${typographyProp.styles.fontSize}px`,
          lineHeight: `${typographyProp.styles.lineHeight}px`,
          fontWeight: weightValue,
        },
      }))
    )
    .flat()
    .reduce((acc, curr) => ({ ...acc, ...curr }), {})
}

export function extractStylesWithPrefix(styleProps: object, prefix: string): string {
  let styles = ''
  Object.entries(styleProps).forEach(([prop, value]) => {
    const multipleFontProperties = prop.toLowerCase().includes('font')
    if (prop === 'font' || multipleFontProperties) {
      Object.entries(value).forEach(([p, v]) => {
        styles += `${prefix}${multipleFontProperties ? `_${prop}` : ''}_${p}: ${v};\n`
      })
    } else {
      styles += `${prefix}_${prop}: ${value};\n`
    }
  })
  return styles
}

export function extractStateStylesWithPrefix(
  stateStyleProps: object,
  prefix: string,
  parentClass: string
): string {
  // TODO: add a mapper for the state names
  // EX: disableUnselected should be => disabled.empty
  // segmented-button and utilities preview screens will need to be updated
  let styles = ''
  Object.values(stateStyleProps).forEach((state: any) => {
    styles += `.${parentClass}.${state.name} {\n`
    Object.entries(state.properties).forEach(([prop, value]) => {
      if (prop === 'font') {
        Object.entries(value).forEach(([p, v]) => {
          styles += `${prefix}_${state.name}_${p}: ${v};\n`
        })
      } else {
        styles += `${prefix}_${state.name}_${prop}: ${value};\n`
      }
    })
    styles += '}\n'
  })
  return styles
}
